import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  UserPlus, Loader2, CheckCircle, XCircle, KeyRound, FolderPlus, Trash2,
  ShieldCheck, Building2, Mail, Search, X, Plus,
} from "lucide-react";

export default function PartnerApprovalsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // partner detail {partner, assigned_folders}
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", company_name: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [assignPf, setAssignPf] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/partner-portal/admin/partners`, { headers });
      setPartners(data.partners || []);
    } catch (e) {
      toast.error("Failed to load partner accounts");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const openDetail = async (p) => {
    try {
      const { data } = await axios.get(`${API_URL}/partner-portal/admin/partners/${p.id}`, { headers });
      setSelected(data);
    } catch (e) {
      toast.error("Failed to load partner");
    }
  };

  const create = async () => {
    if (!form.email || !form.full_name || !form.password) { toast.error("Email, name and password are required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/partner-portal/admin/partners`, form, { headers });
      toast.success("Partner account created & approved");
      setShowCreate(false);
      setForm({ email: "", full_name: "", company_name: "", password: "" });
      fetchPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const setStatus = async (p, action) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/partner-portal/admin/partners/${p.id}/${action}`, {}, { headers });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      fetchPartners();
      if (selected?.partner?.id === p.id) openDetail(p);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const resetPassword = async (p) => {
    const pw = window.prompt(`Set a new password for ${p.email} (min 8 chars):`);
    if (!pw) return;
    try {
      await axios.post(`${API_URL}/partner-portal/admin/partners/${p.id}/reset-password`, { password: pw }, { headers });
      toast.success("Password updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const removePartner = async (p) => {
    if (!window.confirm(`Delete partner account ${p.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/partner-portal/admin/partners/${p.id}`, { headers });
      toast.success("Partner deleted");
      setSelected(null);
      fetchPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const addPf = async () => {
    if (!assignPf.trim() || !selected) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/partner-portal/admin/partners/${selected.partner.id}/assign`,
        { pf: assignPf.trim() }, { headers });
      toast.success(`Shared PF with ${selected.partner.email}`);
      setAssignPf("");
      openDetail(selected.partner);
      fetchPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not share this PF");
    } finally { setBusy(false); }
  };

  const removePf = async (pf) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/partner-portal/admin/partners/${selected.partner.id}/unassign`,
        { pf: pf.id || pf.pf_number }, { headers });
      toast.success("Access removed");
      openDetail(selected.partner);
      fetchPartners();
    } catch (e) {
      toast.error("Failed to remove");
    } finally { setBusy(false); }
  };

  const statusBadge = (s) => {
    const map = {
      approved: "bg-emerald-100 text-emerald-700",
      pending: "bg-amber-100 text-amber-700",
      rejected: "bg-red-100 text-red-700",
    };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-primary" /> Partner Approvals
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              External document-repository accounts. Approve accounts and choose which Position Folders each partner can access.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="partner-create-btn">
            <UserPlus className="h-4 w-4 mr-1" /> New partner account
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>
        ) : partners.length === 0 ? (
          <div className="text-center py-20 bg-card rounded-xl border border-dashed" data-testid="partner-approvals-empty">
            <ShieldCheck className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-foreground font-medium">No partner accounts yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create one to give a vendor access to shared shipment documents.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {partners.map((p) => (
              <div key={p.id} className="bg-card rounded-xl border p-4 shadow-sm" data-testid={`partner-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground truncate flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground shrink-0" />{p.email}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">{p.full_name}{p.company_name ? ` · ${p.company_name}` : ""}</div>
                    <div className="flex items-center gap-2 mt-2">
                      {statusBadge(p.approval_status)}
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><FolderPlus className="h-3 w-3" /> {p.assigned_count} folders</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openDetail(p)} data-testid={`partner-manage-${p.id}`}>
                    <FolderPlus className="h-4 w-4 mr-1" /> Manage folders
                  </Button>
                  {p.approval_status !== "approved" && (
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200" disabled={busy} onClick={() => setStatus(p, "approve")} data-testid={`partner-approve-${p.id}`}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  )}
                  {p.approval_status !== "rejected" && (
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" disabled={busy} onClick={() => setStatus(p, "reject")} data-testid={`partner-reject-${p.id}`}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => resetPassword(p)} data-testid={`partner-resetpw-${p.id}`}>
                    <KeyRound className="h-4 w-4 mr-1" /> Reset password
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removePartner(p)} data-testid={`partner-delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="partner-create-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /> New partner account</h2>
              <button onClick={() => setShowCreate(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-3">
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="customs.base@partner.europacific.com" data-testid="partner-create-email" /></div>
              <div><Label>Full name / contact</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Customs Broker" data-testid="partner-create-name" /></div>
              <div><Label>Company (optional)</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="partner-create-company" /></div>
              <div><Label>Temporary password (min 8)</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="partner-create-password" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={create} disabled={creating} data-testid="partner-create-submit">
                {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Create & approve
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage folders modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-card rounded-xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="partner-manage-modal">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /> {selected.partner.email}</h2>
              <button onClick={() => setSelected(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Share Position Folders with this partner by PF number.</p>

            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={assignPf} onChange={(e) => setAssignPf(e.target.value)} placeholder="e.g. PF-000751-26" className="pl-9"
                  onKeyDown={(e) => e.key === "Enter" && addPf()} data-testid="partner-assign-input" />
              </div>
              <Button onClick={addPf} disabled={busy} data-testid="partner-assign-btn"><Plus className="h-4 w-4 mr-1" /> Share</Button>
            </div>

            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {(selected.assigned_folders || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No folders shared yet.</p>
              ) : selected.assigned_folders.map((pf) => (
                <div key={pf.id} className="flex items-center justify-between bg-muted/40 rounded-lg px-3 py-2" data-testid={`partner-assigned-${pf.pf_number}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{pf.pf_number}</div>
                    <div className="text-xs text-muted-foreground truncate">{pf.customer || pf.description || ""}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removePf(pf)} data-testid={`partner-unassign-${pf.pf_number}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { PartnerApprovalsPage };
